import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { MessengerService } from '@services/messenger/messenger.service';
import { PrivilegeGuard } from '../../guards/privilege.guard';
import { DashboardMessagesService } from '@services/dashboard-messages/dashboard-messages.service';
import { XrefDashboardMessageProfileService } from '@services/dashboard-messages/xref-dashboard-message-profile.service';
import { PackageService } from '@services/packages/package.service';
import { Router } from '@angular/router';
import { MyOrganisationService } from '@services/organisations/my-organisation.service';
import { BannerService } from '@services/banner/banner.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { HttpClient } from '@angular/common/http';
import { MonitoringReportsService } from '@services/monitoring-reports/monitoring-reports.service';
import { XMLParser } from 'fast-xml-parser';
import { Models } from '@ibexfusion/models';
import { timeout } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [DashboardMessagesService, XrefDashboardMessageProfileService, PackageService, MyOrganisationService, BannerService, MonitoringReportsService],
  standalone: false
})
export class DashboardComponent implements OnInit {
  public mediaAssetPath: any;
  public messages = [];
  public ids = { $packageId: null };
  public columns = [
    { prop: 'description', name: 'Description' },
    {
      prop: 'created', name: 'Created', valueTransformer: function(date) {
        return moment(date).format('DD-MM-YYYY  HH:mm');
      }
    }
  ];
  public params: any;
  public showRead = false;
  public activeOrganisation = null;
  public banners;
  public tooManyOrganisations = false;
  public dataForDashboard = [];
  public blogs = [];
  private profileId;
  private roles;
  reports: any[];
  activeBanner = 0;
  organisations;
  messagesFetched;
  ready = false;

  constructor(
    private router: Router, private ms: MessengerService, private priv: PrivilegeGuard, private dashboardMessageService: DashboardMessagesService,
    private xRefService: XrefDashboardMessageProfileService, private packageService: PackageService, private myOrganisationService: MyOrganisationService,
    public bannerService: BannerService, private environmentService: EnvironmentService, private http: HttpClient, private monitoring: MonitoringReportsService
  ) {
    this.myOrganisationService.find(0, 16).then(result => {
      this.tooManyOrganisations = (result?.data?.length || 0) > 15
    });
    this.mediaAssetPath = this.environmentService.getMediaAssetPath();
  }

  public async statusChanged(event) {
    const result = await this.xRefService.find(0, 1, { $and: [{ ProfileID: this.profileId }, { dashboardMessageId: event.target }] });
    if (result?.data?.length) {
      this.xRefService.patch(result.data[0]._id, { hidden: event.value });
    } else {
      const xRefDocument = <Models.XrefDashboardMessageProfile>Models.ModelFactory.createModelInstanceByName('XrefDashboardMessageProfile');
      xRefDocument.populateFromJSON({ ProfileID: this.profileId, dashboardMessageId: event.target, hidden: event.value });
      this.xRefService.create(xRefDocument);
    }
  }

  async ngOnInit() {
    this.profileId = this.ms.$activeProfile.getValue().ProfileID;
    this.ms.$activeRoles.subscribe(roles => {
      this.roles = roles;
      if (roles?.length) {
        this.fetchDashboardMessages(roles);
      }
    });
    this.ms.$dataForDashboard.subscribe(dataForDashboard => {
      this.dataForDashboard = dataForDashboard || [];
    });
    this.activeOrganisation = this.ms.getOrganisation();
    this.organisations = await this.getChildOrganisations(this.activeOrganisation);
    await this.getBanners();
    setInterval(() => { this.activeBanner = (this.activeBanner + 1) % this.banners.length; }, 8000);
    try {
      await this.initMonitoring()
    } catch (err) {
      console.warn('init monitoring failed', err);
    }
    this.ready = true;
    try {
      await this.getBlog();
    } catch (err) {
      console.warn('getting blog failed', err);
    }
    setInterval(() => this.initMonitoring(), 1000 * 60 * 5);
  }

  async initMonitoring() {
    if (this.ms.$activeOrganisation.getValue()?.organisationCustomerId) {
      let packages = [];
      try {
        packages = (await (this.packageService.find(0, 100, { customerId: this.ms.$activeOrganisation.getValue().organisationCustomerId, underConstruction: { $ne: true } }))).data;
      } catch (err) {
        console.log('no packages', err);
        return;
      }
      const reports = (await this.monitoring.find(0, 100, { packageId: { $in: packages.map(el => el._id.toString()) }, seenCount: { $gt: 50 } })).data;
      reports.map(el => {
        el['package'] = packages.find(pak => pak._id === el.packageId);
        return el;
      });
      this.reports = reports;
    }
  }

  async selectOrganisation(org) {
    this.ms.setOrganisation(org);
    await this.router.navigate([{ outlets: { org: org.name, primary: '' } }]);
    await this.router.navigate([{ outlets: { org: org.name, primary: 'dashboard' } }]);
  }

  parentIsActive(org): boolean {
    const parent = this.organisations.find(el => el.OrganisationID === org.ParentOrganisationID);
    return !!parent ? parent.active : true;
  }

  toggleOrgState(org, event: any) {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    org.active = !org.active;
  }

  private async fetchDashboardMessages(roles: any) {
    const targetRoles = roles.map((role: any) => role.roleName);
    const query = { 'targetRole': { $elemMatch: { $in: targetRoles } }, $sort: { priority: -1, _id: -1 } };
    const messages = (await this.dashboardMessageService.find(0, 100, query)).data;
    if (!messages?.length) {
      this.messagesFetched = true;
      return;
    }
    messages.map(message => {
      if (!message.privilege || this.priv.checkIfPrivilege(message.privilege['name'], message.privilege['value'])) {
        message['actionButton'] = this.createAction(message);
        this.checkReadCondition(message).then(status => {
          message['readStatus'] = status;
        });
        message['translation'] = message.translations.length > 1 ? message.translations[0] : null;
        if (!this.messages) {
          this.messages = [];
        }
        this.messages.push(message);
      }
    });
    this.messagesFetched = true;
  }

  private async checkReadCondition(message): Promise<boolean> {
    const query = { $and: [{ ProfileID: this.profileId }, { dashboardMessageId: message._id }] };
    const res = (await this.xRefService.find(0, 1, query)).data;
    return res[0]?.hidden || false;
  }

  private async createAction(message): Promise<any> {
    if (!message.link || !message.action) {
      return null;
    }
    const actionButton = {};
    // todo get language dynamically, now is always german
    actionButton['name'] = message.link.name_de;
    actionButton['target'] = message.link.target || '_blank';
    if (message.action === 'external') {
      actionButton['href'] = message.link['route'];
    } else if (message.action === 'internal') {
      actionButton['route'] = [message.link['route']];
      actionButton['params'] = message.link['params'];
      if (message.link.id) {
        if (message.link.id.indexOf('$package') > -1) {
          const id = await this.getPackageId();
          if (id && this.ids[message.link.id]) {
            actionButton['route'].push(id);
          }
        } else {
          actionButton['route'].push(message.link.id);
        }
      }
    } else if (message.action === 'helpmanual') {
      actionButton['filename'] = message.link['filename'];
      actionButton['tag'] = message.link['tag'];
    }
    return actionButton
  }

  private async getPackageId(): Promise<any> {
    const currentOrg = this.ms.getOrganisation();
    if (!currentOrg) {
      return null;
    }
    const customerId = currentOrg.organisationCustomerId || currentOrg.ibex3customerId;
    if (!customerId) {
      return null;
    }
    const query = currentOrg.ParentOrganisationID ? { customerId: customerId } : { OwnerOrganisationID: currentOrg.OrganisationID };
    const packages = (await this.packageService.find(0, 1, query)).data
    return packages?.length ? packages[0]._id : null;
  }

  toggleReadMessages() {
    this.showRead = !this.showRead;
  }

  async getChildOrganisations(organisation: any) {
    const initialOrganisation = JSON.parse(JSON.stringify(organisation));
    let parentOrganisation = (await this.myOrganisationService.find(0, 1, { OrganisationID: organisation.ParentOrganisationID })).data[0];
    while (organisation?.ParentOrganisationID && parentOrganisation) {
      organisation = (await this.myOrganisationService.find(0, 1, { OrganisationID: organisation.ParentOrganisationID })).data[0];
      parentOrganisation = (await this.myOrganisationService.find(0, 1, { OrganisationID: organisation.ParentOrganisationID })).data[0];
    }
    let subChildren: any[], subSubChildren: any[];
    let children = (await this.myOrganisationService.find(0, 1000, { ParentOrganisationID: organisation.OrganisationID })).data;
    if (children?.length) {
      children = children.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      subChildren = (await this.myOrganisationService.find(0, 1000, { ParentOrganisationID: { $in: children.map(org => org.OrganisationID) } })).data;
      for (const subChild of subChildren) {
        if (children.find(el => el.OrganisationID === subChild.ParentOrganisationID)['children']) {
          children.find(el => el.OrganisationID === subChild.ParentOrganisationID)['children'].push(subChild)
        } else {
          children.find(el => el.OrganisationID === subChild.ParentOrganisationID)['children'] = [subChild];
        }
      }
    }
    if (subChildren?.length) {
      subChildren = subChildren.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      subSubChildren = (await this.myOrganisationService.find(0, 1000, { ParentOrganisationID: { $in: subChildren.map(org => org.OrganisationID) } })).data;
      if (subSubChildren?.length) {
        subSubChildren = subSubChildren.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        for (const subSubChild of subSubChildren) {
          if (subChildren.find(el => el.OrganisationID === subSubChild.ParentOrganisationID)['children']) {
            subChildren.find(el => el.OrganisationID === subSubChild.ParentOrganisationID)['children'].push(subSubChild)
          } else {
            subChildren.find(el => el.OrganisationID === subSubChild.ParentOrganisationID)['children'] = [subSubChild];
          }
        }
      }
    }
    organisation['level'] = 0;
    organisation['children'] = children;
    const items = [organisation];
    for (const child of children as any || []) {
      child['level'] = 1;
      items.push(child);
      for (const subChild of child.children || []) {
        subChild['level'] = 2;
        items.push(subChild);
        for (const subSubChild of subChild.children || []) {
          subSubChild['level'] = 3;
          items.push(subSubChild);
        }
      }
    }
    let parentId = initialOrganisation.ParentOrganisationID;
    const orgId = initialOrganisation.OrganisationID;
    items.find(item => item.OrganisationID === orgId)['active'] = true;
    do {
      const parent = items.find(item => item.OrganisationID === parentId);
      if (parent) {
        parent['active'] = true
      }
      parentId = parent?.ParentOrganisationID;
    } while (!!parentId);
    return items;
  }

  async getBanners() {
    this.banners = (await this.bannerService.find(0, 100, {})).data.map((el: any) => {
      el.imageUrl = this.mediaAssetPath + el.image + '?t=' + Date.now();
      return el;
    }).sort((b1, b2) => {
      return (b1?.order || 0) - (b2?.order || 0);
    });
  }

  async getBlog() {
    const xml = await this.http.get('https://ipeak.ch/rss.xml', { responseType: 'text' }).pipe(timeout(5000)).toPromise();
    const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '', isArray: (tagName, jPath, isLeafNode, isAttribute) => false });
    const response = parser.parse(xml);
    if (response?.rss?.channel?.item?.length) {
      this.blogs = response.rss.channel.item.slice(0, 4);
    }
  }

  openBlog(link) {
    window.open(link, '_blank');
  }

  convertTimestamp(timestamp) {
    return moment(timestamp).format('DD.MM.YYYY - HH:mm');
  }

  getColorClass(timestamp) {
    const difference = new Date().getTime() - timestamp;
    return difference < 1000 * 60 * 15 ? 'green' : difference < 1000 * 60 * 60 ? 'orange' : 'red';
  }

  convertTitle(title) {
    const isMasterPackage = !!title.match(/master/i);
    title = title.replace(/(\*A|\*W):/gi, 'tweebie').replace(/\*S:/gi, 'ibextouch').replace(/(Premium\s)?Touch(\s)?([S|s]creen)?:?/, isMasterPackage ? '' : 'ibextouch');
    if (!!title.match(/master/i)) {
      title = 'Master ' + title.replace(/Master/i, '');
    }
    if (!title.match(/(ibex?(touch|smart|info|tablet)|tweebie|master)/i)) {
      title = 'tweebie ' + title;
    }
    title = title.replace(/tweebie[\s]|^ibex\w*[\s]/, '');
    return title;
  }

  protected readonly status = status;
}
